import React, { useState, useEffect } from "react";
import api from '../api';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Alert from '@mui/material/Alert';
import PrimaryButton from "../components/PrimaryButton";

export default function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [showReset, setShowReset] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState("");
  const [resetConfirmed, setResetConfirmed] = useState(false);
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [pendingMfaToken, setPendingMfaToken] = useState("");
  const [pendingRole, setPendingRole] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const finalizeLogin = (data) => {
    localStorage.setItem("token", data.token);
    if (data.role) {
      localStorage.setItem("adminRole", data.role);
    }
    if (onLogin) onLogin();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await api.post("/admin/api/auth/login", { email, password });
      const data = res.data;
      if (data.success && data.token) {
        finalizeLogin(data);
      } else if (data.mfaRequired && data.pendingToken) {
        setPendingMfaToken(data.pendingToken);
        setPendingRole(data.role || "");
        setMfaCode("");
      } else {
        setError(data.message || "Login failed");
      }
    } catch (err) {
      setError("Network error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    setError("");
    if (!mfaCode.trim()) {
      setError("Enter the 6-digit code from Microsoft Authenticator.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/admin/api/auth/verify-mfa", {
        pendingToken: pendingMfaToken,
        code: mfaCode.trim(),
      });
      const data = res.data;
      if (data.success && data.token) {
        finalizeLogin({ ...data, role: data.role || pendingRole });
      } else {
        setError(data.message || "Invalid authenticator code");
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data || 'Unable to verify authenticator code';
      setError(typeof msg === 'string' ? msg : 'Unable to verify authenticator code');
    } finally {
      setSubmitting(false);
    }
  };

  const resetMfaStep = () => {
    setPendingMfaToken("");
    setPendingRole("");
    setMfaCode("");
    setError("");
  };

  const toggleShowPassword = () => setShowPassword((s) => !s);

  const handleSendReset = async (payloadEmail) => {
    setResetError(''); setResetSuccess('');
    const e = (payloadEmail || '').trim();
    if (!e) { setResetError('Please enter an email'); return; }
    try {
      setResetLoading(true);
      await api.post('/admin/api/admin-users/reset-password', { email: e });
      setResetSuccess("If your email is registered, you'll receive a reset link.");
      setShowReset(false);
      setResetConfirmed(true);
      setEmail('');
    } catch (err) {
      const msg = err.response?.data ?? err.message;
      setResetError(typeof msg === 'string' ? msg : 'Failed to send reset email');
    } finally {
      setResetLoading(false);
    }
  };

  useEffect(() => {
    if (!resetSuccess) return;
    const id = setTimeout(() => setResetSuccess(''), 4000);
    return () => clearTimeout(id);
  }, [resetSuccess]);

  useEffect(() => {
    if (!resetError) return;
    const id = setTimeout(() => setResetError(''), 4000);
    return () => clearTimeout(id);
  }, [resetError]);


  return (
    <Box component="main">
      <Grid container justifyContent="center" sx={{ mt: 12 }}>
        <Grid item xs={12} md={12} lg={10}>
          <Paper elevation={1} sx={{ p: 0, overflow: 'hidden', maxWidth: 840, mx: 'auto' }}>
            <Box sx={{ bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', px: 3, py: { xs: 1.5, md: 2 } }}>
              <Box
                component="img"
                src="ryslogo.png"
                alt="Retool Your School"
                sx={{ height: { xs: 42, md: 60 }, objectFit: 'contain' }}
              />
            </Box>
            <Box sx={{ p: { xs: 4, md: 5 }, px: { xs: 8, md: 12 } }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5, textAlign: 'center' }}>
                {pendingMfaToken ? 'Microsoft Authenticator Verification' : 'Admin Dashboard Login'}
              </Typography>
              {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
              <Box component="form" onSubmit={pendingMfaToken ? handleVerifyMfa : handleSubmit} noValidate>
                <Grid container spacing={2} direction="column">
                  {!pendingMfaToken && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TextField
                          name="email"
                          type="email"
                          label="Email"
                          placeholder="Enter your email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          InputProps={{
                            sx: {
                              height: 56,
                              fontSize: 18,
                              width: 360
                            }
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { fontSize: 18, height: 56, width: 360 } }}
                        />
                      </Box>
                    </Grid>
                  )}
                  {!pendingMfaToken && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TextField
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          label="Password"
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                                  onClick={toggleShowPassword}
                                  edge="end"
                                  size="large"
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                            sx: {
                              height: 56,
                              fontSize: 18,
                              width: 360
                            }
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { fontSize: 18, height: 56, width: 360 } }}
                        />
                      </Box>
                    </Grid>
                  )}
                  {pendingMfaToken && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ maxWidth: 420, textAlign: 'center' }}>
                          Enter the current 6-digit code from the Microsoft Authenticator app to finish signing in.
                        </Typography>
                        <TextField
                          name="mfaCode"
                          label="Authenticator Code"
                          placeholder="123456"
                          value={mfaCode}
                          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          required
                          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                          InputProps={{
                            sx: {
                              height: 56,
                              fontSize: 18,
                              width: 360,
                              letterSpacing: '0.25em'
                            }
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { fontSize: 18, height: 56, width: 360 } }}
                        />
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <PrimaryButton type="submit" sx={{ backgroundColor: '#ff6600', '&:hover': { backgroundColor: '#e65c00' }, fontSize: 18, height: 44, width: 240, mt: 1 }} disabled={submitting}>
                        {pendingMfaToken ? 'VERIFY CODE' : 'LOGIN'}
                      </PrimaryButton>
                      {pendingMfaToken && (
                        <a href="#" onClick={(e) => { e.preventDefault(); resetMfaStep(); }} style={{ display: 'block', textAlign: 'center', marginTop: 8 }}>
                          Use a different account
                        </a>
                      )}
                      <Box>
                        {!pendingMfaToken && !showReset && (
                          <a href="#" onClick={(e) => { e.preventDefault(); setShowReset(true); setResetError(''); setResetSuccess(''); setEmail(''); }} style={{ display: 'block', textAlign: 'center', marginTop: 8 }}>Forgot Password?</a>
                        )}
                        {!pendingMfaToken && showReset && (
                          <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>Forgot your password?</Typography>
                            {resetError ? <Alert severity="error" sx={{ mb: 1 }}>{resetError}</Alert> : null}
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                              <TextField
                                name="resetEmail"
                                type="email"
                                label="Email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                InputProps={{ sx: { height: 56, fontSize: 18, width: 360 } }}
                                sx={{ '& .MuiOutlinedInput-root': { fontSize: 18, height: 56, width: 360 } }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                              <PrimaryButton onClick={() => handleSendReset(email)} disabled={resetLoading || !email} sx={{ backgroundColor: '#ff6600', '&:hover': { backgroundColor: '#e65c00' }, fontSize: 16, height: 40, width: 200 }}>Send Reset Link</PrimaryButton>
                              <PrimaryButton onClick={() => { setShowReset(false); setResetError(''); setResetSuccess(''); setEmail(''); }} sx={{ fontSize: 16, height: 40, width: 120 }}>Cancel</PrimaryButton>
                            </Box>
                          </Box>
                        )}
                        {resetConfirmed && (
                          <Box sx={{ mt: 2 }}>
                            <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                              <Typography>If your email is registered, you'll receive a reset link.</Typography>
                              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                                <Box sx={{ textAlign: 'left', maxWidth: 440 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Didn’t receive your email?</Typography>
                                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                    <li>Check your Junk or Spam folder</li>
                                    <li>Confirm your email address is correct</li>
                                    <li>Click the “Forgot Password?” link to try again</li>
                                  </Box>
                                </Box>
                              </Box>
                            </Paper>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
